import React, { useEffect, useState } from 'react';
import { comentarReplica, guardarReplica, resolverReplica } from '../api';
import { formatMx } from '../utils/datetime';

/**
 * La réplica del Agente a su calificación (ticket #99).
 *
 * UN SOLO COMPONENTE PARA LOS DOS ROLES, A PROPÓSITO
 *   El agente lo ve como formulario; el supervisor, como lectura. Es el mismo
 *   archivo porque lo que el supervisor lee tiene que ser palabra por palabra
 *   lo que el agente escribió — si divergieran, la sesión de retroalimentación
 *   arrancaría discutiendo la herramienta en vez de la llamada. Es la misma
 *   razón por la que `BoletaV2` es compartido.
 *
 * QUIÉN PUEDE ESCRIBIR LO DECIDE EL SERVIDOR
 *   `puedeEscribir` llega en el detalle de la llamada (`puede_replicar`), no se
 *   deduce aquí del rol. La regla ya vive en `guardar_replica`, y una segunda
 *   copia en JavaScript es la manera segura de acabar pintando un formulario
 *   que el API va a rechazar con 403.
 */

const VERDE = '#34d399';
const ROJO = '#f87171';
const GRIS = '#6b7280';

export default function ReplicaPanel({
  callId,
  replica,
  puedeEscribir = false,
  // Comentar es del SUPERVISOR; escribir la réplica es del AGENTE. Son dos
  // permisos distintos y opuestos, y por eso son dos props: quien pasa una no
  // pasa la otra. El backend exige lo mismo — `require_admin` para comentar,
  // "sólo el dueño" para replicar.
  puedeComentar = false,
  onGuardada = null,
  viewerMode = 'admin',
}) {
  const esAgente = viewerMode === 'agent';
  const [editando, setEditando] = useState(false);
  const [deAcuerdo, setDeAcuerdo] = useState(replica?.de_acuerdo ?? true);
  const [motivo, setMotivo] = useState(replica?.motivo ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [resolviendo, setResolviendo] = useState(false);
  const [errorResolucion, setErrorResolucion] = useState(null);
  const [comentario, setComentario] = useState('');
  const [comentando, setComentando] = useState(false);
  const [errorComentario, setErrorComentario] = useState(null);
  const comentarios = replica?.comentarios ?? [];

  // Si el detalle se recarga (por ejemplo tras guardar), el formulario tiene que
  // reflejar lo que quedó en la base y no lo que se tecleó. Sin esto, una
  // escritura que el backend normalizó dejaría la pantalla mostrando algo que
  // no se guardó.
  useEffect(() => {
    setDeAcuerdo(replica?.de_acuerdo ?? true);
    setMotivo(replica?.motivo ?? '');
  }, [replica]);

  // El mismo requisito que exige el `CHECK` de la base y el schema de Pydantic.
  // Aquí sirve para no dejar mandar una petición que va a rebotar; no es la
  // barrera — la barrera está en la base.
  const faltaMotivo = !deAcuerdo && !motivo.trim();

  const guardar = () => {
    setGuardando(true);
    setError(null);
    guardarReplica(callId, { deAcuerdo, motivo })
      .then(() => {
        setEditando(false);
        if (onGuardada) onGuardada();
      })
      .catch((e) => setError(
        e?.response?.data?.detail
          ? String(e.response.data.detail)
          : 'No se pudo guardar tu réplica. Inténtalo de nuevo.'
      ))
      .finally(() => setGuardando(false));
  };

  const resolver = (valor) => {
    setResolviendo(true);
    setErrorResolucion(null);
    resolverReplica(callId, valor)
      .then(() => { if (onGuardada) onGuardada(); })
      .catch((e) => setErrorResolucion(
        e?.response?.data?.detail
          ? String(e.response.data.detail)
          : 'No se pudo guardar la resolución.'
      ))
      .finally(() => setResolviendo(false));
  };

  const enviarComentario = () => {
    setComentando(true);
    setErrorComentario(null);
    comentarReplica(callId, comentario)
      .then(() => {
        setComentario('');
        if (onGuardada) onGuardada();
      })
      .catch((e) => setErrorComentario(
        e?.response?.data?.detail
          ? String(e.response.data.detail)
          : 'No se pudo guardar el comentario.'
      ))
      .finally(() => setComentando(false));
  };

  // Nada que mostrar: ni hay réplica ni este usuario puede escribirla. Es el
  // caso del supervisor en una llamada que el agente todavía no ha visto.
  if (!replica && !puedeEscribir) {
    return null;
  }

  const mostrandoFormulario = puedeEscribir && (editando || !replica);

  return (
    <div
      className="card"
      style={{
        marginTop: 16,
        borderLeft: `3px solid ${replica ? (replica.de_acuerdo ? VERDE : ROJO) : '#818cf8'}`,
      }}
    >
      <div className="card-title">
        {esAgente ? '¿Estás de acuerdo con esta calificación?' : 'Réplica del Agente'}
      </div>

      {!mostrandoFormulario && replica && (
        <>
          {/* EL DESENLACE, arriba y con autor. Es lo primero que el
              supervisor necesita saber al abrir la llamada: si esto ya se
              atendió o no. Sin él, una réplica resuelta se veía idéntica a una
              recién escrita. */}
          {replica.resolucion && (
            <div style={{
              display: 'inline-block', marginBottom: 10, padding: '3px 10px',
              borderRadius: 999, fontSize: 11.5, fontWeight: 700,
              border: `1px solid ${replica.resolucion === 'aceptada' ? VERDE : GRIS}`,
              color: replica.resolucion === 'aceptada' ? VERDE : '#9ca3af',
              background: replica.resolucion === 'aceptada'
                ? 'rgba(52,211,153,0.10)' : 'rgba(107,114,128,0.10)',
            }}>
              {/* En segunda persona para el agente. "Se le dio razón al
                  agente" en la pantalla del propio agente lo pone a leer sobre
                  sí mismo en tercera persona, que es la voz de un expediente y
                  no de una respuesta. */}
              {replica.resolucion === 'aceptada'
                ? (esAgente ? 'Se te dio razón' : 'Se le dio razón al agente')
                : (esAgente ? 'Se sostuvo la calificación' : 'Se sostiene la calificación')}
              {replica.resuelta_por ? ` · ${replica.resuelta_por}` : ''}
              {replica.resuelta_at ? ` · ${formatMx(replica.resuelta_at)}` : ''}
            </div>
          )}
          {!replica.resolucion && !replica.de_acuerdo && (
            <div style={{
              display: 'inline-block', marginBottom: 10, padding: '3px 10px',
              borderRadius: 999, fontSize: 11.5, fontWeight: 700,
              border: '1px solid #fbbf24', color: '#fbbf24',
              background: 'rgba(251,191,36,0.10)',
            }}>
              {esAgente ? 'Tu supervisor aún no la ha revisado' : 'Pendiente de revisar'}
            </div>
          )}

          {esAgente && replica.resolucion === 'aceptada' && (
            <p style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.6, margin: '0 0 10px' }}>
              Tu supervisor revisó lo que escribiste y te dio razón. La
              calificación de esta llamada no cambia — lo que se corrige es el
              criterio, y para eso sirve tu réplica.
            </p>
          )}

          <div style={{ fontSize: 13.5, fontWeight: 600, color: replica.de_acuerdo ? VERDE : ROJO }}>
            {replica.de_acuerdo
              ? (esAgente ? 'Registraste que estás de acuerdo.' : 'El agente está de acuerdo con la calificación.')
              : (esAgente ? 'Registraste que NO estás de acuerdo.' : 'El agente NO está de acuerdo con la calificación.')}
          </div>


          {replica.motivo && (
            <p style={{ fontSize: 13, lineHeight: 1.7, color: '#e0e0e6', marginTop: 10, marginBottom: 0, fontStyle: 'italic' }}>
              «{replica.motivo}»
            </p>
          )}

          <div style={{ fontSize: 11, color: GRIS, marginTop: 10 }}>
            Registrada el {formatMx(replica.updated_at || replica.created_at)}
            {replica.updated_at && replica.created_at
              && replica.updated_at !== replica.created_at
              && ' (modificada)'}
          </div>

          {/* La respuesta del supervisor. Va DEBAJO de lo que dijo el agente:
              primero se lee la postura, luego lo que se le contestó. Al revés
              sería leer la respuesta antes de la pregunta.

              El agente los ve. Es lo que cierra el circuito: escribe, le
              responden, y lo lee. Sin esto, hablar no tiene consecuencia
              visible y la réplica se convierte en un buzón. */}
          {(comentarios.length > 0 || puedeComentar) && (
            <div style={{ marginTop: 16, borderTop: '1px solid #26262e', paddingTop: 12 }}>
              <div style={{
                fontSize: 9.5, letterSpacing: 0.5, textTransform: 'uppercase',
                color: GRIS, fontWeight: 600, marginBottom: 8,
              }}>
                {comentarios.length > 0
                  ? 'Comentarios del supervisor'
                  : 'Responder a la réplica'}
              </div>

              {comentarios.map((c) => (
                <div key={c.id} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: '#a5b4fc' }}>
                    {c.autor} · {formatMx(c.created_at)}
                  </div>
                  <p style={{
                    fontSize: 13, lineHeight: 1.65, color: '#e0e0e6',
                    margin: '2px 0 0',
                  }}>
                    {c.texto}
                  </p>
                </div>
              ))}

              {/* La ACCIÓN. Va con el hilo porque es la misma conversación:
                  se responde y se cierra.

                  El texto dice explícitamente que no mueve la nota. Un
                  "aceptada" junto a una calificación que no cambió, sin
                  explicación, se lee como burla — y hoy el único veredicto que
                  un supervisor puede mover es el del error. */}
              {puedeComentar && !replica.de_acuerdo && (
                <div style={{ marginTop: 4, marginBottom: 12 }}>
                  <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 6 }}>
                    ¿Qué se decidió? Queda registrado con tu nombre.
                    {' '}<span style={{ color: GRIS }}>
                      No cambia la calificación: sirve para calibrar el criterio.
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                      { valor: 'aceptada', label: 'Le doy razón', color: VERDE },
                      { valor: 'rechazada', label: 'Sostengo la calificación', color: GRIS },
                    ].map((op) => (
                      <button
                        key={op.valor}
                        type="button"
                        disabled={resolviendo}
                        onClick={() => resolver(op.valor)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          cursor: resolviendo ? 'not-allowed' : 'pointer',
                          border: `1px solid ${replica.resolucion === op.valor ? op.color : '#2d2d44'}`,
                          background: replica.resolucion === op.valor ? `${op.color}22` : 'transparent',
                          color: replica.resolucion === op.valor ? op.color : '#94a3b8',
                        }}
                      >
                        {op.label}
                      </button>
                    ))}
                    {replica.resolucion && (
                      <button
                        type="button"
                        disabled={resolviendo}
                        onClick={() => resolver(null)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, fontSize: 12,
                          border: '1px solid #2d2d44', background: 'transparent',
                          color: '#6b7280', cursor: 'pointer',
                        }}
                      >
                        Volver a pendiente
                      </button>
                    )}
                  </div>
                  {errorResolucion && (
                    <div style={{ marginTop: 6, fontSize: 12, color: ROJO }}>{errorResolucion}</div>
                  )}
                </div>
              )}

              {puedeComentar && (
                <div style={{ marginTop: 10 }}>
                  <textarea
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    rows={3}
                    placeholder="Lo que respondes se guarda con tu nombre y el agente lo ve. No se puede editar después."
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 6,
                      border: '1px solid #2d2d44', background: '#16161d', color: '#e0e0e6',
                      fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical',
                    }}
                  />
                  {errorComentario && (
                    <div style={{ marginTop: 6, fontSize: 12, color: ROJO }}>{errorComentario}</div>
                  )}
                  <button
                    type="button"
                    onClick={enviarComentario}
                    disabled={comentando || !comentario.trim()}
                    style={{
                      marginTop: 8, padding: '6px 16px', borderRadius: 6, border: 'none',
                      fontSize: 12.5, fontWeight: 700,
                      cursor: comentando || !comentario.trim() ? 'not-allowed' : 'pointer',
                      background: comentando || !comentario.trim() ? '#2d2d44' : '#818cf8',
                      color: comentando || !comentario.trim() ? '#6b7280' : '#0f0f13',
                    }}
                  >
                    {comentando ? 'Guardando...' : 'Comentar'}
                  </button>
                </div>
              )}
            </div>
          )}

          {puedeEscribir && (
            <button
              type="button"
              onClick={() => setEditando(true)}
              style={{
                marginTop: 12, padding: '5px 12px', borderRadius: 6,
                border: '1px solid #2d2d44', background: 'rgba(255,255,255,0.03)',
                color: '#a5b4fc', fontSize: 12, cursor: 'pointer',
              }}
            >
              Cambiar mi respuesta
            </button>
          )}
        </>
      )}

      {mostrandoFormulario && (
        <>
          <p style={{ fontSize: 12.5, color: '#9ca3af', lineHeight: 1.65, marginTop: 0 }}>
            Tu respuesta la ve tu supervisor <strong>antes</strong> de la sesión de
            retroalimentación. Si no estás de acuerdo, di por qué: cada desacuerdo se
            revisa, y en conjunto sirven para corregir los criterios que están mal
            redactados. No afecta tu calificación por sí solo.
          </p>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {[
              { valor: true, label: 'Estoy de acuerdo', color: VERDE },
              { valor: false, label: 'No estoy de acuerdo', color: ROJO },
            ].map((op) => (
              <button
                key={String(op.valor)}
                type="button"
                onClick={() => setDeAcuerdo(op.valor)}
                style={{
                  padding: '7px 16px', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${deAcuerdo === op.valor ? op.color : '#2d2d44'}`,
                  background: deAcuerdo === op.valor ? `${op.color}22` : 'transparent',
                  color: deAcuerdo === op.valor ? op.color : '#94a3b8',
                }}
              >
                {op.label}
              </button>
            ))}
          </div>

          {/* El campo va SIEMPRE, no sólo al disentir. Es obligatorio nada más
              cuando no estás de acuerdo —lo exige el CHECK de la base— pero
              quien está de acuerdo también puede tener algo que contar, y
              esconderle el campo es decirle que no hay dónde. */}
          {true && (
            <>
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 6 }}>
                  {deAcuerdo
                    ? 'Algo que quieras agregar (opcional)'
                    : <>Por qué no estás de acuerdo <span style={{ color: ROJO }}>*</span></>}
                </div>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={4}
                  placeholder="Ej.: sí expliqué la política de cancelación, en el minuto 3, cuando el cliente preguntó por el reembolso."
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 6,
                    border: '1px solid #2d2d44', background: '#16161d', color: '#e0e0e6',
                    fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical',
                  }}
                />
              </div>
            </>
          )}

          {error && (
            <div style={{ marginTop: 10, fontSize: 12, color: ROJO }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
            <button
              type="button"
              onClick={guardar}
              disabled={guardando || faltaMotivo}
              title={faltaMotivo ? 'Para no estar de acuerdo hace falta escribir el motivo' : undefined}
              style={{
                padding: '7px 18px', borderRadius: 6, border: 'none', fontSize: 12.5,
                fontWeight: 700,
                cursor: guardando || faltaMotivo ? 'not-allowed' : 'pointer',
                background: guardando || faltaMotivo ? '#2d2d44' : '#818cf8',
                color: guardando || faltaMotivo ? '#6b7280' : '#0f0f13',
              }}
            >
              {guardando ? 'Guardando...' : replica ? 'Guardar cambios' : 'Enviar mi respuesta'}
            </button>
            {replica && (
              <button
                type="button"
                onClick={() => { setEditando(false); setError(null); }}
                style={{
                  padding: '7px 14px', borderRadius: 6, border: '1px solid #2d2d44',
                  background: 'transparent', color: '#94a3b8', fontSize: 12.5, cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
            )}
            {faltaMotivo && (
              <span style={{ fontSize: 11.5, color: '#fbbf24' }}>
                Escribe el motivo para poder enviar.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
